import {
  types, applySnapshot, addDisposer,
} from 'mobx-state-tree';
import { reaction } from 'mobx';
import { Position } from 'deck.gl';
import debounce from 'lodash/debounce';
import WebMercatorViewport, { Coordinates } from 'viewport-mercator-project';
import { LngLat, LngLatBounds } from 'mapbox-gl';
import * as Sentry from '@sentry/browser';
import { Theme } from '@routific/hive';
import { VisitStatus } from '../../../types/TVisit';
import {
  VisitDtoModel,
  VisitDtoModelType,
  MapMarkerType,
  RouteTimelineDtoModelType,
  RouteDtoModelType,
} from '../models';
import getAppRoot from '../utils/getAppRoot';
import getBoundsFromRoutes from '../../../helpers/routes/getBoundsFromRoutes';
import * as layers from './models/layers';
import * as lasso from './models/lasso';
import * as draw from './models/draw';
import { ItemsArray, ItemsArrayModelType } from './models/ItemsArray';
import { isPositionInViewingArea } from '../../../helpers/isPositionInViewingArea';
import { getItemizedOrders, getItemizedStops } from './helpers/getItems';
import { getSelectedItemIdsFromMarkers } from './helpers/markerSelection';
import { RouteDriverLocation } from '../DriverLocation';
import getPanelPropertiesAndDimensions, { panelPropertiesFromInsets } from './helpers/getPanelPropertiesAndDimensions';
import { MapFitChromeInsets } from '../../../helpers/mapFitChrome';
import getOffsetCoordinates from './helpers/getOffsetCoordinates';
import computeMapPosition from './helpers/computeMapPosition';
import { ViewStateProps } from '@deck.gl/core/lib/deck';
import { ViewState } from 'react-map-gl';
import { getMapIconPreference, setMapIconPreference } from './helpers/initialMapIconViewPreference';
import { loadMapPopupEnabledValue, saveMapPopupEnabledValue } from './helpers/mapPopupEnabledPreferences';
import {
  logInvalidContainerError,
  logNullComputedPositionError,
  logNullBoundsError,
  logSetMapPositionAttempt,
  logMapRecenteringSuccess,
  logMapOperationDuringInit,
  logCoordinateCenteringSuccess,
  logRouteSelectionMapRefError,
} from '../../../helpers/mapRecentering/logger';

export const MAP_DEFAULT_ZOOM = 12;
export const MAP_MAX_ZOOM = 17;
export const MAP_MIN_ZOOM = 3;

const TRANSITION_DURATION = 500;

export const DEFAULT_VIEW_STATE_LOCATION = {
  latitude: 49.29036,
  longitude: -123.15404,
};

export type PositionUndefinedNull = Position | undefined | null;

const VisitArray = types.array(types.safeReference(types.late(() => VisitDtoModel), { acceptsUndefined: false }));

const onToggleStopMapPopup = (self: any) => {
  addDisposer(self, reaction(
    () => self.toggledViewMapPopup,
    debounce((activePopup: boolean) => {
      if (!activePopup) {
        self.setPopoverItems(undefined);
      }
    }, self.toggledViewMapPopup ? 0 : 300),
  ));

  return {};
};

const onMarkerFocus = (self: any) => {
  addDisposer(self, reaction(
    () => self.hoveredMarker,
    (hoveredMarker) => {
      if (self.disabledMapPopup) return;
      if (hoveredMarker) {
        const items = hoveredMarker?.itemsIds ?? [hoveredMarker?.id];
        self.setPopoverItems({
          unscheduledOrders: hoveredMarker?.type === 'unscheduled' ? items : undefined,
          stops: hoveredMarker?.type === 'delivering_stop' ? items : undefined,
          driver: hoveredMarker.driverName ? hoveredMarker : undefined,
        });
      }
      self.toggleStopMapPopup(!!hoveredMarker);
    },
  ));

  return {};
};

// A start/end location change on the selected route records it in
// forcePanRouteId; the fit runs when that route's geometry actually updates,
// not on an incidental re-render (test case 2397, scenario 3). The id is
// checked against the selected route by uuid or id, so selecting another
// route, or deselecting, drops it instead of fitting whatever comes next.
const onForcedRoutePan = (self: any) => {
  addDisposer(self, reaction(
    () => {
      const route = getAppRoot(self).ui.selectedRoute;
      return {
        forcePanRouteId: self.forcePanRouteId as string | undefined,
        routeId: route?.id as string | undefined,
        routeUuid: route?.uuid as string | undefined,
        polyline: route?.routeSolution?.polyline as string | undefined,
      };
    },
    ({
      forcePanRouteId, routeId, routeUuid, polyline,
    }, previous) => {
      if (!forcePanRouteId) return;
      if (forcePanRouteId !== routeId && forcePanRouteId !== routeUuid) {
        self.setForcePanRouteId(undefined);
        return;
      }
      if (polyline === previous?.polyline) return;

      self.setForcePanRouteId(undefined);
      const route = getAppRoot(self).ui.selectedRoute;
      const bounds = route ? getBoundsFromRoutes([route]) : null;
      if (bounds) {
        self.setMapPosition(bounds);
      }
    },
  ));

  return {};
};

const onMarkersSelected = (self: any) => {
  addDisposer(self, reaction(
    () => self.selectedMarkers,
    (selectedMarkers) => {
      if (selectedMarkers) {
        self.setSelectedItems(getSelectedItemIdsFromMarkers(selectedMarkers));
      } else {
        self.setSelectedItems(undefined);
      }
    },
  ));

  return {};
};

export enum Panel {
  LEFT = 'left',
  RIGHT = 'right',
  BOTTOM = 'bottom',
}

const MapView = types.model('MapView')
  .actions(self => ({
    reset: () => {
      applySnapshot(self, {});
    },
  }))
  .props(lasso.props)
  .views(lasso.views)
  .actions(lasso.actions)
  .actions(lasso.onChangeLassoMode)
  .actions(onToggleStopMapPopup)
  .views(layers.views)
  .actions(layers.actions)
  .props(draw.props)
  .views(draw.views)
  .actions(draw.actions)
  .props({
    reactMapGLRef: types.maybeNull(types.frozen<any>()),
    viewState: types.frozen<ViewState>({
      ...DEFAULT_VIEW_STATE_LOCATION,
      zoom: MAP_DEFAULT_ZOOM,
      bearing: 0,
      pitch: 0,
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
      },
    }),
    selectedMarkers: types.maybe(types.frozen<MapMarkerType[]>()),
    hoveredMarker: types.maybe(types.frozen<MapMarkerType>()),
    hoveredSuggestion: types.maybe(types.frozen<MapMarkerType>()),
    hoveredLngLat: types.maybeNull(types.maybe(types.frozen<Position>())),
    isDraggable: types.optional(types.boolean, true),
    draggingVisit: types.maybe(types.frozen<MapMarkerType>()),
    hoveredMarkerVisitsList: types.maybe(VisitArray),
    popoverItems: types.maybe(ItemsArray),
    isLoadingMarkerVisits: types.optional(types.boolean, false),
    toggledViewMapPopup: types.optional(types.boolean, false),
    toggledViewStopIcons: types.optional(types.boolean, getMapIconPreference()),
    isDraggingMap: types.optional(types.boolean, false),
    mapPopupEnabled: types.optional(types.boolean, loadMapPopupEnabledValue()),
  })
  .volatile(() => ({
    skipNextRoutePan: false,
    // The route (uuid or id) a start/end location change is known to extend;
    // onForcedRoutePan fits it once its geometry updates, provided it is still
    // the selected route.
    forcePanRouteId: undefined as string | undefined,
  }))
  .views((self) => ({
    get viewport(): WebMercatorViewport {
      const mapWidth = self.reactMapGLRef?.getMap()._containerWidth;
      const mapHeight = self.reactMapGLRef?.getMap()._containerHeight;

      return new WebMercatorViewport({ ...self.viewState, width: mapWidth || 100, height: mapHeight || 100 });
    },
    get bounds(): LngLatBounds {
      const bounds = new LngLatBounds();

      const nw = this.viewport.unproject([0, 0]);
      const se = this.viewport.unproject([self.reactMapGLRef?.getMap()._containerWidth, self.reactMapGLRef?.getMap()._containerHeight]);

      if (nw && se) {
        bounds.extend(new LngLat(...nw));
        bounds.extend(new LngLat(...se));
      }

      return bounds;
    },
    isPointVisible(coordinates: Coordinates): boolean {
      // `project` input is in [lng, lat] and returned in [x, y]
      const positionOnScreenInPixels: Coordinates = this.viewport.project(coordinates);

      const panelPropertiesAndDimensions = getPanelPropertiesAndDimensions(getAppRoot(self).ui.panelProperties);

      return isPositionInViewingArea({
        bounds: this.bounds,
        coordinates,
        position: positionOnScreenInPixels,
        mapWidth: self.reactMapGLRef?.getMap()._containerWidth,
        mapHeight: self.reactMapGLRef?.getMap()._containerHeight,
        isLeftPanelOpen: panelPropertiesAndDimensions.isLeftPanelOpen,
        isRightPanelOpen: panelPropertiesAndDimensions.isRightPanelOpen,
        isBottomPanelOpen: panelPropertiesAndDimensions.isBottomPanelOpen,
        leftPanelWidth: panelPropertiesAndDimensions.left,
        rightPanelWidth: panelPropertiesAndDimensions.right,
        bottomPanelHeight: panelPropertiesAndDimensions.bottom,
      });
    },
    areBoundsPartiallyVisible(bounds: LngLatBounds): boolean {
      const map = self.reactMapGLRef?.getMap();
      if (!map) return false;

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const center = bounds.getCenter();

      // Target smaller than (or overlapping the edge of) the viewport: some
      // corner or the centre is on screen.
      if (
        this.isPointVisible([center.lng, center.lat])
        || this.isPointVisible([sw.lng, sw.lat])
        || this.isPointVisible([ne.lng, ne.lat])
        || this.isPointVisible([sw.lng, ne.lat])
        || this.isPointVisible([ne.lng, sw.lat])
      ) {
        return true;
      }

      // Viewport inside the target (zoomed into one route of a wide day): no
      // corner is on screen, but the visible area still overlaps the bounds.
      // Use the map area not hidden behind open trays so an overlap that is
      // only under a panel doesn't count.
      const panels = getPanelPropertiesAndDimensions(getAppRoot(self).ui.panelProperties);
      const bottomInset = panels.isBottomPanelOpen ? panels.bottom + parseInt(Theme.Spacing.mdSpacing, 10) : 0;
      const nw = this.viewport.unproject([panels.left, 0]);
      const se = this.viewport.unproject([map._containerWidth - panels.right, map._containerHeight - bottomInset]);
      if (!nw || !se) return false;

      const visible = new LngLatBounds().extend(new LngLat(...nw)).extend(new LngLat(...se));
      return (
        visible.getWest() <= ne.lng
        && visible.getEast() >= sw.lng
        && visible.getSouth() <= ne.lat
        && visible.getNorth() >= sw.lat
      );
    },
    isZoomBeyondThreshold(): boolean {
      return self.viewState.zoom < layers.ZOOM_THRESHOLD_FOR_MARKERS;
    },
    isInSelectionMode(): boolean {
      return !!self.lassoMode || !!self.draggingVisit;
    },
  }))
  .actions((self) => ({
    setSkipNextRoutePan(value: boolean) {
      self.skipNextRoutePan = value;
    },
    setForcePanRouteId(routeId: string | undefined) {
      self.forcePanRouteId = routeId;
    },
    zoomIn() {
      const zoomInLevel = self.viewState.zoom ? Math.min(MAP_MAX_ZOOM, self.viewState.zoom + 1) : MAP_MAX_ZOOM;

      self.viewState = { ...self.viewState, zoom: zoomInLevel };
    },
    zoomOut() {
      const zoomOutLevel = self.viewState.zoom ? Math.max(MAP_MIN_ZOOM, self.viewState.zoom - 1) : MAP_MIN_ZOOM;

      self.viewState = { ...self.viewState, zoom: zoomOutLevel };
    },
    toggleViewStopIcons() {
      const isMarkerVisible = !self.toggledViewStopIcons;
      self.toggledViewStopIcons = isMarkerVisible;
      setMapIconPreference(isMarkerVisible);
    },
    setMapPopupEnabled(value: boolean) {
      self.mapPopupEnabled = value;
      saveMapPopupEnabledValue(value);
    },
    hoverMarker(mapMarker?: MapMarkerType) {
      self.hoveredMarker = mapMarker;
    },
    selectMarkers(mapMarkers?: MapMarkerType[]) {
      self.selectedMarkers = mapMarkers;
    },
    setViewState(viewState: ViewStateProps) {
      self.viewState = { ...self.viewState, ...viewState };
    },
    setMapPosition(
      mapBounds: LngLatBounds | null,
      fitExactBounds?: boolean,
      callerSource?: string,
      chrome?: MapFitChromeInsets,
    ) {
      // Always log when setMapPosition is called for debugging
      const appRoot = getAppRoot(self);
      logSetMapPositionAttempt({
        bounds: mapBounds,
        fitExactBounds,
        hasReactMapGLRef: !!self.reactMapGLRef,
        selectedRoute: appRoot.ui.selectedRoute,
        isInitializing: appRoot.ui.areOrdersOrRoutesLoadingOnInit,
      });

      if (!self.reactMapGLRef) {
        const isInitializing = appRoot.ui.areOrdersOrRoutesLoadingOnInit;
        
        // Log specific error when called from useRouteSelection with null reactMapGLRef
        if (callerSource === 'useRouteSelection') {
          logRouteSelectionMapRefError({
            bounds: mapBounds,
            route: appRoot.ui.selectedRoute,
            selectedRouteCount: appRoot.ui.selectedRoutesBulk.size,
            triggerSource: callerSource,
            isInitializing,
            viewState: self.viewState,
          });
        } else if (isInitializing) {
          logMapOperationDuringInit({
            operation: 'setMapPosition',
            bounds: mapBounds,
            isInitializing,
          });
        }
        return;
      }

      const containerWidth = self.reactMapGLRef.getMap()._containerWidth;
      const containerHeight = self.reactMapGLRef.getMap()._containerHeight;

      if (containerWidth <= 0 || containerHeight <= 0) {
        logInvalidContainerError({
          containerWidth,
          containerHeight,
          bounds: mapBounds,
          mapElement: {
            exists: !!self.reactMapGLRef?.getMap()?.getContainer(),
            clientWidth: self.reactMapGLRef?.getMap()?.getContainer()?.clientWidth,
            clientHeight: self.reactMapGLRef?.getMap()?.getContainer()?.clientHeight,
          },
          viewState: self.viewState,
        });
        return;
      }

      const { geolocation } = getAppRoot(self).ui;

      const panelPropertiesAndDimensions = chrome
        ? panelPropertiesFromInsets(chrome)
        : getPanelPropertiesAndDimensions(getAppRoot(self).ui.panelProperties);

      const computedMapPosition = computeMapPosition(
        mapBounds,
        geolocation,
        panelPropertiesAndDimensions,
        containerWidth,
        containerHeight,
        self.viewState.zoom,
        fitExactBounds,
      );

      if (computedMapPosition) {
        const { latitude, longitude, zoom } = computedMapPosition;
        self.reactMapGLRef.flyTo({ center: [longitude, latitude], zoom, duration: TRANSITION_DURATION });
        
        logMapRecenteringSuccess({
          latitude,
          longitude,
          zoom,
          bounds: mapBounds,
          containerWidth,
          containerHeight,
          fitExactBounds,
        });
      } else {
        logNullComputedPositionError({
          bounds: mapBounds,
          geolocation,
          panelPropertiesAndDimensions,
          containerWidth,
          containerHeight,
          viewState: self.viewState,
          fitExactBounds,
        });
      }
    },
    centerMapOnCoordinates(longitude: number, latitude: number, force: boolean = false) {
      // If selected lng lat is not in view of current map, re-center the map
      if (!self.reactMapGLRef?.getMap()) {
        // Only log if this seems like a real issue (not just initialization timing)
        const appRoot = getAppRoot(self);
        const isInitializing = appRoot.ui.areOrdersOrRoutesLoadingOnInit;
        
        if (!isInitializing) {
          Sentry.captureException(new Error('Map coordinate centering failed: reactMapGLRef or map is null after initialization'), {
            tags: { mapRecentering: 'failed' },
            extra: {
              reason: 'null_map_ref_or_map_post_init',
              longitude,
              latitude,
              force,
              hasReactMapGLRef: !!self.reactMapGLRef,
              viewState: self.viewState,
              isInitializing,
            },
          });
        } else {
          // Track attempts during initialization for analysis
          Sentry.addBreadcrumb({
            message: 'Map operation attempted during initialization',
            category: 'mapRecentering',
            level: 'info',
            data: {
              operation: 'centerMapOnCoordinates',
              longitude,
              latitude,
              force,
              isInitializing,
            },
          });
        }
        return;
      }

      if (!self.bounds) {
        logNullBoundsError({
          longitude,
          latitude,
          force,
          viewState: self.viewState,
          containerDimensions: {
            width: self.reactMapGLRef?.getMap()._containerWidth,
            height: self.reactMapGLRef?.getMap()._containerHeight,
          },
        });
        return;
      }

      const isVisible = self.isPointVisible([longitude, latitude]);
      if (!isVisible || force) {
        const {
          isRightPanelOpen, isLeftPanelOpen, isBottomPanelOpen, currentLeftPanelWidth, currentRightPanelWidth, currentBottomPanelHeight,
        } = getPanelPropertiesAndDimensions(getAppRoot(self).ui.panelProperties);

        const panelProps = {
          isRightPanelOpen, isLeftPanelOpen, isBottomPanelOpen, currentLeftPanelWidth, currentRightPanelWidth, currentBottomPanelHeight,
        };

        const offsetCoordinates = getOffsetCoordinates({
          longitude,
          latitude,
          viewport: self.viewport,
          viewStateZoom: self.viewState.zoom,
          panelProperties: panelProps,
        });

        self.reactMapGLRef.flyTo({ center: [offsetCoordinates[0], offsetCoordinates[1]], duration: TRANSITION_DURATION });
        
        logCoordinateCenteringSuccess({
          longitude,
          latitude,
          force,
          wasVisible: isVisible,
          offsetCoordinates,
          panelProps,
        });
      }
    },
  }))
  .actions((self) => {
    // Bring a stop or order into view without changing zoom, but only when
    // none of the given points is already on screen (test case 2396).
    const revealPoints = (points: Coordinates[]) => {
      if (!self.reactMapGLRef?.getMap() || points.length === 0) return;
      if (points.some((point) => self.isPointVisible(point))) return;
      const [longitude, latitude] = points[0];
      self.centerMapOnCoordinates(longitude, latitude, false);
    };

    return {
      revealPoints,
      revealOrders(orderUuids: string[]) {
        if (!self.reactMapGLRef?.getMap()) return;
        const { model } = getAppRoot(self);
        const visits = Array.from<VisitDtoModelType>(model.visitDtos.values());
        const stops = Array.from<RouteTimelineDtoModelType>(model.routeTimelineDtos.values());
        const points = orderUuids
          .map((orderUuid): Coordinates | undefined => {
            const location = visits.find((visit) => visit.uuid === orderUuid)?.activeLocation;
            if (location?.longitude != null && location?.latitude != null) {
              return [location.longitude, location.latitude];
            }
            return stops.find((stop) => stop.orderUuid === orderUuid)?.coordinates;
          })
          .filter((point): point is Coordinates => !!point);
        revealPoints(points);
      },
      revealStops(eventUuids: string[]) {
        if (!self.reactMapGLRef?.getMap()) return;
        const { model } = getAppRoot(self);
        const stops = Array.from<RouteTimelineDtoModelType>(model.routeTimelineDtos.values());
        const points = eventUuids
          .map((eventUuid) => stops.find((stop) => stop.eventUuid === eventUuid)?.coordinates)
          .filter((point): point is Coordinates => !!point);
        revealPoints(points);
      },
      // Fit the given routes only when no part of them is in view (test case 2397).
      revealRoutes(routeUuids: string[]) {
        if (!self.reactMapGLRef?.getMap()) return;
        const { model } = getAppRoot(self);
        const routes = routeUuids
          .map((routeUuid) => model.routeByUuid(routeUuid))
          .filter((route): route is RouteDtoModelType => !!route);
        const bounds = getBoundsFromRoutes(routes);
        if (bounds && !self.areBoundsPartiallyVisible(bounds)) {
          self.setMapPosition(bounds);
        }
      },
    };
  })
  .actions(onForcedRoutePan)
  .actions(onMarkerFocus)
  .actions(onMarkersSelected)
  .views((self) => ({
    get sortedPopoverItems(): ItemsArrayModelType | undefined {
      return {
        stops: self.popoverItems?.stops?.slice().sort((a, b) => (!!a?.stopSequence && !!b?.stopSequence ? a.stopSequence - b.stopSequence : 0)),
        unscheduledOrders: self.popoverItems?.unscheduledOrders,
        driver: self.popoverItems?.driver,
      } as ItemsArrayModelType;
    },
    canDragMarker(marker: MapMarkerType): boolean {
      const { canMoveStopsPermission } = getAppRoot(self).account;
      const isDraggedWithinSelection = self.isMarkerInLasso(marker);

      if (!canMoveStopsPermission) {
        return false;
      }

      const status = marker.properties?.status;
      if (status === VisitStatus.DEPARTED || status === VisitStatus.ARRIVED) {
        return false;
      }

      if (marker.properties?.cluster) {
        return false;
      }

      if (!isDraggedWithinSelection && self.lassoMode) {
        return false;
      }

      if (self.isDrawMode) {
        return false;
      }

      return true;
    },
  }))
  .actions((self) => ({
    setMapRefs(ref: any) {
      self.reactMapGLRef = ref?.reactMapGLRef || null;
    },
    dragVisit(mapMarker: MapMarkerType | undefined) {
      self.draggingVisit = mapMarker;
    },
    toggleStopMapPopup(hovered: boolean) {
      self.toggledViewMapPopup = hovered;
    },
    toggleViewStopsIcon(viewStops: boolean) {
      self.toggledViewStopIcons = viewStops;
    },
    setIsDraggable(draggable: boolean) {
      self.isDraggable = draggable;
    },
    setHoveredLngLat(coordinates: PositionUndefinedNull) {
      self.hoveredLngLat = coordinates;
    },
    setPopoverItems(items?: { unscheduledOrders?: string[], stops?: string[], driver?: RouteDriverLocation }) {
      self.popoverItems = undefined;
      if (items) {
        self.isLoadingMarkerVisits = true;
        const popoverItems: { stops: RouteTimelineDtoModelType[], unscheduledOrders: VisitDtoModelType[], driver?: RouteDriverLocation } = { unscheduledOrders: [], stops: [] };

        popoverItems.stops = getItemizedStops(self, self.popoverItems, items.stops);
        popoverItems.unscheduledOrders = getItemizedOrders(self, self.popoverItems, items.unscheduledOrders);
        popoverItems.driver = items.driver;

        self.popoverItems = popoverItems ? popoverItems as ItemsArrayModelType : undefined;
        self.isLoadingMarkerVisits = false;
      }
    },
  }));

export default MapView;
